import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowRight, ArrowLeft, DownloadSimple, Lock } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SignaturePad } from "../components/SignaturePad";
import ListenButton from "../components/ListenButton";
import { LessonBody } from "../components/LessonBody";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const videoEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

export default function PublicTrainingPage() {
  const { token } = useParams();
  const [topic, setTopic] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState("intro");
  const [verticalVideo, setVerticalVideo] = useState(false);
  const [qi, setQi] = useState(0);
  const [answers, setAnswers] = useState({});
  const [linksChecked, setLinksChecked] = useState([]);
  const [attest, setAttest] = useState({ first_name: "", last_name: "", employee_id: "", email: "", typed_name: "" });
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/training/public/${token}`).then((r) => setTopic(r.data)).catch(() => setNotFound(true));
  }, [token]);

  const submit = async () => {
    if (!attest.first_name.trim() || !attest.last_name.trim() || !attest.email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    if (!attest.employee_id.trim()) { toast.error("Please enter your employee ID"); return; }
    if (!sig) { toast.error("Please sign before submitting"); return; }
    if (!attest.typed_name.trim()) { toast.error("Type your full name to confirm your signature"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/training/public/${token}/submit`, { ...attest, typed_signature: attest.typed_name, signature_png: sig, answers, links_attested: linksChecked });
      setResult(data);
      setStep("done");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const retake = () => {
    setAnswers({});
    setQi(0);
    setResult(null);
    setStep(topic.questions.length ? "quiz" : "attest");
    window.scrollTo(0, 0);
  };

  const shell = (children) => (
    <div className="min-h-screen bg-background">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-6">
          <img src="/shc-logo.png" alt="Silver Home Care" className="h-9 w-auto" />
          <span className="font-heading text-lg font-semibold tracking-tight text-stone-900">Silver Home Care <span className="font-normal text-stone-500">Staff Training</span></span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10">{children}</main>
    </div>
  );

  if (notFound) {
    return shell(
      <div className="rounded-lg border border-stone-200 bg-white p-10 text-center shadow-sm" data-testid="training-not-found">
        <p className="font-heading text-xl font-semibold text-stone-900">Training not found</p>
        <p className="mt-2 text-sm text-stone-500">This link may have been deactivated. Contact the office for a current link.</p>
      </div>
    );
  }
  if (!topic) {
    return shell(<div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>);
  }

  const embed = videoEmbedUrl(topic.video_url);
  const graded = topic.mode === "graded";
  const total = topic.questions.length;
  const q = total ? topic.questions[Math.min(qi, total - 1)] : null;
  const picked = q ? answers[q.id] : undefined;
  const pickedCorrect = !graded && q && picked !== undefined && picked === q.correct_index;
  const pickedWrong = !graded && q && picked !== undefined && picked !== q.correct_index;

  if (step === "done" && result) {
    return shell(
      <div className="space-y-6" data-testid="training-result">
        <div className={`rounded-lg border p-8 text-center shadow-sm ${result.passed ? "border-[#27AE60]/30 bg-[#27AE60]/5" : "border-[#C0392B]/30 bg-[#C0392B]/5"}`}>
          {result.passed ? (
            <>
              <div className="rounded-xl border-4 border-double border-[#C9A227] bg-white p-8 text-center shadow-lg" data-testid="certificate-popup">
                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-stone-400">Silver Home Care</p>
                <h2 className="mt-2 font-heading text-2xl font-semibold text-stone-900">Certificate of Completion</h2>
                <p className="mt-4 text-xs uppercase tracking-wide text-stone-400">This certifies that</p>
                <p className="mt-1 font-heading text-3xl font-semibold text-primary">{attest.first_name} {attest.last_name}</p>
                <p className="mt-4 text-xs uppercase tracking-wide text-stone-400">has successfully completed the annual training</p>
                <p className="mt-1 font-heading text-xl font-semibold text-stone-900">{topic.title}</p>
                <p className="mt-4 text-xs text-stone-500">{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}{graded ? ` · Score ${result.score}%` : ""}</p>
                <a href={`${BACKEND}/api/training/public/certificate/${result.completion_id}`} target="_blank" rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
                  data-testid="download-certificate">
                  <DownloadSimple size={16} /> Download certificate (PDF)
                </a>
              </div>
              <div className="rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-5 text-center shadow-sm">
                <CheckCircle size={28} weight="fill" className="mx-auto text-[#27AE60]" />
                <p className="mt-2 text-sm font-medium text-stone-700" data-testid="result-score">
                  {(result.remediations || []).length
                    ? "Competency passed — a rewatch has been assigned."
                    : "Your signed attestation has been filed to your employee record."}
                </p>
                {result.queued && (
                  <p className="mt-2 text-xs text-[#8a5d14]" data-testid="result-queued">
                    That employee ID didn't match a record — no problem. Your completion is filed and the office will match it to you.
                  </p>
                )}
                {result.spin_awarded && (
                  <a href="/wrap-wheel" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#4A235A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:scale-[1.03]"
                    data-testid="result-spin-award">
                    You earned a bonus prize-wheel spin — take it for a spin →
                  </a>
                )}
              </div>
            </>
          ) : result.locked ? (
            <>
              <Lock size={44} weight="fill" className="mx-auto text-[#C0392B]" />
              <h1 className="mt-3 font-heading text-3xl font-semibold text-stone-900">No attempts remaining</h1>
              <p className="mt-2 text-sm text-stone-600" data-testid="result-locked">
                You scored {result.score}% (passing: {result.passing_score}%). The office has been notified and will follow up with retraining.
              </p>
            </>
          ) : (
            <>
              <XCircle size={44} weight="fill" className="mx-auto text-[#C0392B]" />
              <h1 className="mt-3 font-heading text-3xl font-semibold text-stone-900">Not quite — {result.score}%</h1>
              <p className="mt-2 text-sm text-stone-600" data-testid="result-failed">
                Passing score is {result.passing_score}%.{result.attempts_left != null ? ` You have ${result.attempts_left} attempt${result.attempts_left === 1 ? "" : "s"} left.` : ""}
              </p>
              {(result.remediations || []).length ? (
                <a href="/onboarding" className="mt-5 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-stone-900" data-testid="failed-goto-onboarding">
                  Complete the assigned rewatch
                </a>
              ) : (
                <Button onClick={retake} className="mt-5" data-testid="retake-button"><ArrowLeft size={16} className="mr-1.5" /> Review and try again</Button>
              )}
            </>
          )}
        </div>
        {(result.remediations || []).length > 0 && (
          <div className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-6 shadow-sm" data-testid="remediation-assignments">
            <p className="font-heading text-sm font-semibold text-[#8a5d14]">Assigned rewatch before onboarding is complete</p>
            <div className="mt-3 space-y-2">
              {result.remediations.map((r) => (
                <div key={r.id} className="rounded-lg border border-[#B7791F]/20 bg-white p-3 text-sm" data-testid={`assigned-remediation-${r.id}`}>
                  <p className="font-medium text-stone-900">{r.title}</p>
                  <p className="mt-0.5 text-xs text-stone-500">{r.wrong_count} missed · due {String(r.due_at).slice(0, 10)}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-stone-600">You may continue working until the due date. Open Onboarding Training, rewatch the assigned topic, and sign each video again.</p>
            <a href="/onboarding" className="mt-4 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-stone-900" data-testid="goto-onboarding-remediation">
              Go to Onboarding Training
            </a>
          </div>
        )}
        {(result.review || []).length > 0 && (
          <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="result-review">
            <p className="font-heading text-sm font-semibold text-stone-900">Answer review</p>
            <div className="mt-3 space-y-4">
              {topic.questions.map((qq) => {
                const rv = result.review.find((x) => x.id === qq.id);
                if (!rv) return null;
                return (
                  <div key={qq.id} className="border-b border-stone-100 pb-3 last:border-0" data-testid={`review-${qq.id}`}>
                    <p className="text-sm font-medium text-stone-900">{qq.text}</p>
                    <p className="mt-1 text-sm text-[#1e8449]">Correct answer: {qq.choices[rv.correct_index]}</p>
                    {rv.explanation && <p className="mt-1 text-xs text-stone-500">{rv.explanation}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return shell(
    <div className="space-y-6" data-testid="public-training-page">
      <div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">{topic.title}</h1>
        {topic.description && <p className="mt-2 text-sm text-stone-600">{topic.description}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs font-semibold" data-testid="training-steps">
          {[["intro", "1 · Watch / read"], ["quiz", "2 · Quick questions"], ["attest", "3 · Sign & finish"]].map(([key, label]) => (
            <span key={key} className={`rounded-full px-3 py-1.5 ${step === key ? "bg-primary text-primary-foreground" : "bg-stone-100 text-stone-400"}`} data-testid={`step-${key}`}>
              {label}
            </span>
          ))}
        </div>
      </div>

      {step === "intro" && (
        <div className="space-y-5" data-testid="training-intro">
          {embed ? (
            <div className="overflow-hidden rounded-lg border border-stone-200 bg-black shadow-sm">
              <iframe src={embed} title={topic.title} className="aspect-video w-full" allowFullScreen data-testid="training-video" />
            </div>
          ) : topic.video_url && topic.video_url.includes("/api/") ? (
            <div className={`flex justify-center rounded-lg bg-stone-900 shadow-sm ${verticalVideo ? "px-4 py-6 sm:px-10" : ""}`}>
              <video controls preload="metadata"
                onLoadedMetadata={(e) => setVerticalVideo(e.currentTarget.videoHeight > e.currentTarget.videoWidth)}
                className={verticalVideo
                  ? "aspect-[9/16] max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
                  : "aspect-video w-full rounded-lg"}
                data-testid="training-video-file">
                <source src={`${topic.video_url}${topic.video_url.includes("?") ? "&" : "?"}v=3`} type="video/mp4" />
                Your browser can't play this video — please call the office.
              </video>
            </div>
          ) : topic.video_url ? (
            <a href={topic.video_url} target="_blank" rel="noreferrer" className="block rounded-lg border border-stone-200 bg-white p-5 text-sm font-medium text-primary shadow-sm hover:underline" data-testid="training-video-link">
              Watch the training video
            </a>
          ) : null}
          {topic.has_audio && (
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="training-voiceover">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Narrated lesson — listen to the full training</p>
              <audio controls preload="metadata" className="mt-2 w-full" data-testid="training-voiceover-player"
                src={`${BACKEND}/api/training/public/${token}/media/audio`} />
            </div>
          )}
          {topic.reading && (
            <div className="overflow-hidden rounded-lg border border-stone-200 bg-accent/30 shadow-sm" data-testid="training-reading">
              {topic.art && <img src={topic.art} alt="" className="h-32 w-full object-cover sm:h-40" data-testid="training-art" />}
              <div className="p-5">
              <div className="mb-4 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Read the training</p>
                <ListenButton text={topic.reading} testid="training-reading-listen" label="Listen instead" />
              </div>
              <LessonBody reading={topic.reading} />
              </div>
            </div>
          )}
          {(topic.resource_links || []).length > 0 && (
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="training-resource-links">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Watch each training, then confirm</p>
              <div className="mt-3 space-y-2.5">
                {topic.resource_links.map((l, i) => (
                  <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-100 bg-accent/40 px-3 py-2.5" data-testid={`training-link-${i}`}>
                    <a href={l.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary hover:underline" data-testid={`training-link-open-${i}`}>
                      {l.title || `Training video ${i + 1}`} ↗
                    </a>
                    <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-stone-600" data-testid={`training-link-attest-${i}`}>
                      <input type="checkbox" checked={linksChecked.includes(String(i))}
                        onChange={(e) => setLinksChecked((c) => (e.target.checked ? [...c, String(i)] : c.filter((x) => x !== String(i))))} />
                      {(l.url || "").endsWith(".pdf") ? "I read and understood this guide" : "I watched and understood this training"}
                    </label>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-stone-400">{linksChecked.length} of {topic.resource_links.length} confirmed</p>
            </div>
          )}
          <div className="rounded-lg border border-stone-200 bg-white p-5 text-sm text-stone-600 shadow-sm">
            {total > 0
              ? graded
                ? `This is a graded competency quiz: ${total} questions, passing score ${topic.passing_score}%. Answers are not shown during the quiz.`
                : `${topic.video_url ? "After the video" : "After the reading"} there is a short ${total}-question quiz. If you miss one, we'll show you the correct answer so you can fix it — the goal is reinforcement, not tricking you.`
              : topic.reading && !topic.video_url
                ? "Read or listen to the training, then sign the attestation below."
                : "Watch the video, then sign the attestation below."}
          </div>
          <button onClick={() => setStep(total ? "quiz" : "attest")}
            disabled={(topic.resource_links || []).length > 0 && linksChecked.length < topic.resource_links.length}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground shadow-md transition hover:opacity-90 disabled:opacity-40"
            data-testid="start-quiz-button">
            {total ? "I'm done watching — continue to the questions" : "Continue to the signature"} <ArrowRight size={20} />
          </button>
          {(topic.resource_links || []).length > 0 && linksChecked.length < topic.resource_links.length && (
            <p className="text-center text-xs text-stone-400">Confirm each video above to continue.</p>
          )}
        </div>
      )}

      {step === "quiz" && q && (
        <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="quiz-step">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Question {qi + 1} of {total}</p>
          <p className="mt-2 text-base font-medium text-stone-900" data-testid="quiz-question-text">{q.text}</p>
          <div className="mt-4 space-y-2">
            {q.choices.map((choice, ci) => {
              const selected = picked === ci;
              let cls = "border-stone-200 hover:border-stone-400";
              if (selected && graded) cls = "border-primary bg-accent";
              if (!graded && picked !== undefined) {
                if (ci === q.correct_index && (selected || pickedWrong)) cls = "border-[#27AE60] bg-[#27AE60]/10";
                else if (selected) cls = "border-[#C0392B] bg-[#C0392B]/10";
                else cls = "border-stone-200 opacity-70";
              }
              return (
                <button key={ci} onClick={() => setAnswers((a) => ({ ...a, [q.id]: ci }))}
                  className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-sm text-stone-800 transition-colors ${cls}`}
                  data-testid={`quiz-choice-${qi}-${ci}`}>
                  <span>{choice}</span>
                  {!graded && selected && pickedCorrect && <CheckCircle size={18} weight="fill" className="shrink-0 text-[#27AE60]" />}
                  {!graded && selected && pickedWrong && <XCircle size={18} weight="fill" className="shrink-0 text-[#C0392B]" />}
                </button>
              );
            })}
          </div>
          {!graded && pickedWrong && (
            <div className="mt-4 rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-4" data-testid="quiz-feedback-wrong">
              <p className="text-sm font-semibold text-[#C0392B]">Not quite — the correct answer is highlighted above.</p>
              {q.explanation && <p className="mt-1 text-sm text-stone-600">{q.explanation}</p>}
              <p className="mt-1 text-xs text-stone-500">Pick the correct answer to continue.</p>
            </div>
          )}
          {!graded && pickedCorrect && q.explanation && (
            <div className="mt-4 rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-4" data-testid="quiz-feedback-correct">
              <p className="text-sm font-semibold text-[#1e8449]">Correct!</p>
              <p className="mt-1 text-sm text-stone-600">{q.explanation}</p>
            </div>
          )}
          <div className="mt-5 flex items-center justify-between">
            <button onClick={() => setQi((i) => Math.max(0, i - 1))} disabled={qi === 0}
              className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900 disabled:opacity-40" data-testid="quiz-prev">
              <ArrowLeft size={14} /> Back
            </button>
            <Button
              disabled={picked === undefined || (!graded && !pickedCorrect)}
              onClick={() => (qi + 1 < total ? setQi(qi + 1) : setStep("attest"))}
              data-testid="quiz-next">
              {qi + 1 < total ? "Next" : "Continue to sign"} <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </div>
        </div>
      )}

      {step === "attest" && (
        <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="attest-step">
          <p className="text-sm text-stone-700">
            Please enter your name to attest that you have watched and understand the information presented in the <span className="font-medium">{topic.title}</span>.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="attest-first">First name</Label>
              <Input id="attest-first" value={attest.first_name} onChange={(e) => setAttest((a) => ({ ...a, first_name: e.target.value }))} data-testid="attest-first-name" />
            </div>
            <div>
              <Label htmlFor="attest-last">Last name</Label>
              <Input id="attest-last" value={attest.last_name} onChange={(e) => setAttest((a) => ({ ...a, last_name: e.target.value }))} data-testid="attest-last-name" />
            </div>
            <div>
              <Label htmlFor="attest-eid">Employee ID#</Label>
              <Input id="attest-eid" value={attest.employee_id} onChange={(e) => setAttest((a) => ({ ...a, employee_id: e.target.value }))} data-testid="attest-employee-id" />
              <p className="mt-1 text-xs text-stone-400">This matches your completion to your employee record.</p>
            </div>
            <div>
              <Label htmlFor="attest-email">Email</Label>
              <Input id="attest-email" type="email" value={attest.email} onChange={(e) => setAttest((a) => ({ ...a, email: e.target.value }))} data-testid="attest-email" />
            </div>
          </div>
          <div className="mt-4">
            <Label>Sign legibly</Label>
            <SignaturePad onChange={setSig} />
          </div>
          <div className="mt-4">
            <Label htmlFor="attest-typed">Type your full legal name to confirm your signature</Label>
            <Input id="attest-typed" value={attest.typed_name} onChange={(e) => setAttest((a) => ({ ...a, typed_name: e.target.value }))}
              placeholder="e.g. Jane A. Doe" data-testid="attest-typed-name" />
            <p className="mt-1 text-xs text-stone-400">This prints beneath your signature on the certificate so your signature is always legible — it counts as your electronic signature.</p>
          </div>
          <div className="mt-5 flex items-center justify-between">
            <button onClick={() => (total ? (setQi(total - 1), setStep("quiz")) : setStep("intro"))}
              className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="attest-back">
              <ArrowLeft size={14} /> Back
            </button>
            <Button onClick={submit} disabled={busy} data-testid="attest-submit">
              {busy ? "Submitting..." : "Submit training"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
